import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import type { Lead } from '@shared/types';
import { formatDate } from '@shared/dates';
import { useStages } from '../store';
import { matchesQuery, normalizeQuery, sortByDateDesc, type GroupedLeads } from '../filter';
import { Bidi, Empty, Icons } from './ui';
import { StagePill } from './StagePill';
import { NotesBubble } from './Notes';
import { TasksButton } from './Tasks';
import { GroupTabs, scrollToGroupTabs } from './MobileCards';

const COLS = '32px minmax(160px,1.4fr) 104px minmax(150px,1.2fr) 130px minmax(160px,1.2fr) 96px minmax(120px,1fr) 110px';
const ROW_H = 40;
/** Height of the sticky app header the group headers stick under. */
const TOP_OFFSET = 53;
const GROUP_HEADER_H = 44;

export type Selection = { selected: Set<string>; toggle: (id: string) => void; setMany: (ids: string[], on: boolean) => void };

/** Height of the tab bar (44px tabs + its border) the group header sticks under. */
const TABS_H = 45;

/**
 * Desktop: the groups as tabs (the same bar as on mobile) and the open group's table below. A new search or filter that
 * leaves the open tab empty jumps to the first tab with results.
 */
export function GroupedTable({ groups, selection, onOpen, active, onSelect, filterKey }: {
  groups: GroupedLeads[]; selection: Selection; onOpen: (id: string) => void; active: string | null; onSelect: (groupId: string) => void; filterKey: string;
}) {
  const current = groups.find((g) => g.group.id === active) ?? groups[0];
  useEffect(() => {
    if (current && current.leads.length === 0) { const hit = groups.find((g) => g.leads.length > 0); if (hit) onSelect(hit.group.id); }
  }, [filterKey]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!current) return <p className="p-8 text-center text-ink-3">אין לידים להצגה</p>;
  return (
    <div className="px-1">
      <GroupTabs groups={groups} active={current.group.id} onSelect={(id) => { onSelect(id); scrollToGroupTabs(); }} top={TOP_OFFSET} />
      <GroupSection key={current.group.id} data={current} selection={selection} onOpen={onOpen} />
    </div>
  );
}

/**
 * The open group's table. All of its rows are rendered (virtualized against the page scroll, no inner scrollbar and no
 * pagination); the group header and the column header stay pinned under the tabs while you scroll through the group.
 */
function GroupSection({ data, selection, onOpen }: { data: GroupedLeads; selection: Selection; onOpen: (id: string) => void }) {
  const [q, setQ] = useState('');
  const rows = useMemo(() => {
    const nq = normalizeQuery(q);
    return sortByDateDesc(nq.text ? data.leads.filter((l) => matchesQuery(l, nq)) : data.leads);
  }, [data.leads, q]);
  const allSelected = rows.length > 0 && rows.every((r) => selection.selected.has(r.id));

  const listRef = useRef<HTMLDivElement>(null);
  const [margin, setMargin] = useState(0);
  useLayoutEffect(() => { setMargin((listRef.current?.getBoundingClientRect().top ?? 0) + window.scrollY); }, [rows.length]);
  const v = useWindowVirtualizer({ count: rows.length, estimateSize: () => ROW_H, overscan: 10, scrollMargin: margin });
  const top = TOP_OFFSET + TABS_H;

  return (
    <section className="card overflow-visible" role="tabpanel" aria-label={data.group.name}>
      <header className="sticky z-20 flex flex-wrap items-center gap-2 rounded-t-xl border-b border-line bg-page px-3 py-2" style={{ top, minHeight: GROUP_HEADER_H }}>
        <span className="flex items-center gap-2 font-semibold">
          {data.group.name}
          <span className="rounded-full bg-panel px-2 py-0.5 text-xs font-medium text-ink-2">{q ? `${rows.length} / ${data.leads.length}` : data.leads.length}</span>
        </span>
        <div className="relative ms-auto w-56">
          <input className="input h-8 min-h-0 ps-8 text-sm" placeholder="חיפוש בקבוצה…" value={q} onChange={(e) => setQ(e.target.value)} aria-label={`חיפוש ב${data.group.name}`} />
          <Icons.Search size={14} className="pointer-events-none absolute inset-y-0 my-auto start-2.5 text-ink-3" />
        </div>
      </header>
      <div className="lead-row sticky z-10 bg-panel text-xs font-semibold text-ink-2" style={{ gridTemplateColumns: COLS, minHeight: 32, top: top + GROUP_HEADER_H }}>
        <div className="cell"><input type="checkbox" aria-label="בחר את כל הקבוצה" checked={allSelected} onChange={(e) => selection.setMany(rows.map((r) => r.id), e.target.checked)} /></div>
        <div className="cell">שם מלא</div><div className="cell">הערות · משימות</div><div className="cell">סטטוס</div><div className="cell">טלפון</div>
        <div className="cell">אימייל</div><div className="cell">תאריך</div><div className="cell">מקור הליד</div><div className="cell">אחראי</div>
      </div>
      <div ref={listRef} role="table" style={{ height: v.getTotalSize(), position: 'relative' }}>
        {v.getVirtualItems().map((it) => {
          const lead = rows[it.index];
          return (
            <div key={lead.id} style={{ position: 'absolute', top: 0, insetInlineStart: 0, width: '100%', transform: `translateY(${it.start - margin}px)`, height: ROW_H }}>
              <LeadRow lead={lead} selected={selection.selected.has(lead.id)} toggle={() => selection.toggle(lead.id)} onOpen={() => onOpen(lead.id)} />
            </div>
          );
        })}
      </div>
      {rows.length === 0 && <p className="p-4 text-center text-sm text-ink-3">אין לידים</p>}
    </section>
  );
}

function LeadRow({ lead, selected, toggle, onOpen }: { lead: Lead; selected: boolean; toggle: () => void; onOpen: () => void }) {
  const { userName } = useStages();
  return (
    <div role="row" tabIndex={0} className={`lead-row ${selected ? 'selected' : ''}`} style={{ gridTemplateColumns: COLS, height: ROW_H }}
      onClick={onOpen} onKeyDown={(e) => { if (e.key === 'Enter') onOpen(); }}>
      <div className="cell" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={selected} onChange={toggle} aria-label={`בחר ${lead.fullName}`} /></div>
      <div className="cell font-medium"><Bidi>{lead.fullName || <Empty />}</Bidi></div>
      <div className="cell gap-1"><NotesBubble lead={lead} /><TasksButton lead={lead} /></div>
      <div className="cell"><StagePill lead={lead} onChanged={onOpen} /></div>
      <div className="cell">{lead.phone ? <a className="ltr text-accent hover:underline" href={`tel:${lead.phone}`} onClick={(e) => e.stopPropagation()}>{lead.phone}</a> : <Empty />}</div>
      <div className="cell">{lead.email ? <Bidi ltr className="text-ink-2">{lead.email}</Bidi> : <Empty />}</div>
      <div className="cell ltr text-ink-2">{formatDate(lead.leadDate)}</div>
      <div className="cell text-ink-2"><Bidi>{lead.source ?? <Empty />}</Bidi></div>
      <div className="cell text-ink-2">{lead.assignedTo ? userName(lead.assignedTo) : <Empty />}</div>
    </div>
  );
}
